import { useEffect, useRef } from "react"

import { Flex, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import {
  menuDataId,
  menuItemDataIdPrefix
} from "@/organisms/Ticketing/components/DynamicTagsField/const"

import styles from "../DynamicTagsField.module.scss"

import type { DynamicTag } from "@/api/useWorkflow.types"
import type { MenuDynamicTagProps } from "../DynamicTagsField.types"

export const DynamicTagsMenu = (props: MenuDynamicTagProps) => {
  const { style, tags, replaceWithClosedTag } = props
  const ref = useRef<HTMLDivElement | null>(null)
  const { t } = useTranslation()

  useEffect(() => {
    const menu = ref.current

    if (!menu) return

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key
      const isDown = key === "ArrowDown"
      const isUp = key === "ArrowUp"

      if (!isDown && !isUp) return

      e.preventDefault()
      e.stopPropagation()

      const activeElement = document.activeElement as HTMLElement | null
      const containsActive = menu.contains(activeElement)

      if (isDown) {
        const firstChild = menu.firstChild as HTMLElement | null
        const nextSibling =
          (menu.firstChild?.nextSibling as HTMLElement | null) || firstChild

        if (!containsActive && nextSibling) return nextSibling.focus()

        if (activeElement?.nextSibling)
          (activeElement.nextSibling as HTMLElement).focus()
        else if (firstChild) firstChild.focus()
      }

      if (isUp) {
        const lastChild = menu.lastChild as HTMLElement | null

        if (!containsActive && lastChild) return lastChild.focus()

        if (activeElement?.previousSibling)
          (activeElement.previousSibling as HTMLElement).focus()
        else {
          const lastChild = menu.lastChild as HTMLElement | null

          if (lastChild) lastChild.focus()
        }
      }
    }

    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node

      if (menu.contains(target)) return

      props.close()
    }

    const keydownEvent = "keydown"
    const clickEvent = "click"
    const optionsWithCapture = { capture: true }

    window.addEventListener(keydownEvent, handleKeyDown, optionsWithCapture)
    window.addEventListener(clickEvent, onClickOutside, optionsWithCapture)

    if (!props.activeNodeKey)
      setTimeout(() => {
        ;(menu.firstChild as HTMLElement)?.focus()
      }, 50)

    return () => {
      window.removeEventListener(clickEvent, onClickOutside, optionsWithCapture)
      window.removeEventListener(
        keydownEvent,
        handleKeyDown,
        optionsWithCapture
      )
    }
  }, [])

  const onClick = (tag: DynamicTag) =>
    replaceWithClosedTag(tag, { root: !props.activeNodeKey })

  return (
    <Flex
      vertical
      ref={ref}
      tabIndex={0}
      style={style}
      className={styles.menu}
      data-id={menuDataId}
    >
      {(tags || []).map((tag, index) => {
        const dataId = `${menuItemDataIdPrefix}${index}`

        return (
          <Flex
            vertical
            data-id={dataId}
            tabIndex={0}
            key={tag.key}
            className={styles.menuTag}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onClick(tag)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                onClick(tag)
              }
            }}
            onMouseEnter={() =>
              (
                ref.current?.querySelector(
                  `[data-id="${dataId}"]`
                ) as HTMLDivElement
              )?.focus()
            }
          >
            <span>{tag.displayName}</span>
            <span className={styles.menuTagDescription}>{tag.description}</span>
          </Flex>
        )
      })}
      {!tags?.length && (
        <Flex className={styles.noResults} align="center" justify="center">
          <Text italic type="secondary">
            {t("jiraTemplateModal.dynamicTags.notFound")}
          </Text>
        </Flex>
      )}
    </Flex>
  )
}
